import * as React from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

const columns: GridColDef[] = [
  {
    field: "id",
    headerName: "id",
    maxWidth: 40,
    minWidth: 40,
    flex: 0.5,
    editable: false,
  },
  {
    field: "heading",
    headerName: "Category",
    minWidth: 150,
    flex: 2,
    editable: false,
  },
  {
    field: "option",
    headerName: "Permission",
    width: 150,
    flex: 2,
    editable: false,
  },
  {
    field: "description",
    headerName: "Description",
    width: 200,
    flex: 4,
    editable: false,
  },
  {
    field: "enabled",
    headerName: "Enabled",
    type: "boolean",
    width: 110,
    flex: 1,
    editable: false,
    renderCell: (params) => {
      return params.value ? (
        <CheckIcon color="success" />
      ) : (
        <CloseIcon color="error" />
      );
    },
  },
  {
    field: "rawOption",
    headerName: "Key",
    width: 150,
    flex: 2,
    editable: false,
  },
];

interface Option {
  key: string;
  name: string;
  description?: string;
}

interface Group {
  heading: string;
  requisiteOptions?: string[];
  options: Option[];
}

// The order, name and description of the opermissions list comes from
// https://github.com/SafetyCulture/frontend-reactor/blob/e2a3c6c63770a2dbdb7f53507899a36b6ab82c2a/packages/sc-organisation/src/components/permission-sets/permission-constants.ts#L167
export const PermissionOptionsList: Group[] = [
  {
    heading: "Platform management",
    options: [
      {
        key: "manage_role",
        name: "Organization",
        description:
          "Manage organization settings, like single sign-on or custom branding, and set up integrations.",
      },
      {
        key: "billing",
        name: "Billing",
        description:
          "Manage billing settings, like your organization’s billing details or payment methods, and view tax invoices.",
      },
      {
        key: "manage_permissions",
        name: "Permissions",
        description: "Add, remove, and manage permission sets.",
      },
      {
        key: "manage_users",
        name: "Users",
        description:
          "Add and deactivate users, as well as manage user details and settings.",
      },
      {
        key: "manage_credentials",
        name: "Credentials",
        description:
          "View credential media files, add and approve credentials, as well as manage credential types.",
      },
      {
        key: "manage_folders",
        name: "Sites",
        description:
          "Create, edit, and delete sites, as well as manage site membership.",
      },
      {
        key: "create_children",
        name: "Groups",
        description:
          "Create, edit, and delete groups, as well as manage group membership.",
      },
    ],
  },
  {
    heading: "Templates",
    options: [
      {
        key: "create_template",
        name: "Create",
        description: "Create new templates and import from the Public Library.",
      },
      {
        key: "manage_response_sets",
        name: "Global Response Sets",
        description: "Create, edit, and delete Global Response Sets.",
      },
      {
        key: "share_public_library",
        name: "Upload to Public Library",
        description:
          "Upload or delete templates from the Public Library and Shared Library.",
      },
      {
        key: "manage_report_preferences",
        name: "Report layouts",
        description:
          "Create, edit, and delete report layouts that are available to all templates.",
      },
    ],
  },
  {
    heading: "Training",
    options: [
      {
        key: "manage_training",
        name: "Administration",
        description:
          "Manage settings and certificates, as well as view analytics.",
      },
      {
        key: "manage_training_teams",
        name: "Manage teams",
        description:
          "Facilitate team-specific training, assessments, group training, and view analytics for your assigned teams. Go to Training > Manage teams to assign a manager.",
      },
      {
        key: "create_training",
        name: "Manage content",
        description:
          "Create, edit, assign, and publish courses, quizzes, and paths.",
      },
      {
        key: "create_course",
        name: "Create courses (restricted)",
        description:
          'Create and share courses. Please note publishing courses requires the "Manage content" permission.',
      },
      {
        key: "view_training",
        name: "Access",
        description: "Access the Training feature to take courses and quizzes.",
      },
    ],
  },
  {
    heading: "Actions", // aka Tasks
    options: [
      {
        key: "manage_tasks",
        name: "Manage action settings",
      },
    ],
  },
  {
    heading: "Issues",
    options: [
      {
        key: "manage_incidents",
        name: "Settings",
        description:
          "Manage issue settings, including categories, access, and QR codes.",
      },
      {
        key: "incident_access",
        name: "Access",
        description: "Access the Issues feature to report and view issues.",
      },
    ],
  },
  {
    heading: "Schedules",
    options: [
      {
        key: "manage_schedules",
        name: "Manage",
        description:
          "View, edit, and delete all schedules in your organization.",
      },
      {
        key: "create_schedules",
        name: "Create",
        description:
          "Create, edit, and delete schedules. Each user can only manage the schedules that they’ve created.",
      },
    ],
  },
  {
    heading: "Sensors",
    options: [
      {
        key: "manage_sensors",
        name: "Manage",
        description:
          "Add, edit, or remove sensors, gateways, and weather data feeds.",
      },
      {
        key: "view_sensors",
        name: "Access",
        description:
          "Access the Sensors feature to view sensors, gateways, and weather data feeds.",
      },
    ],
  },
  {
    heading: "Assets",
    options: [
      {
        key: "settings_assets",
        name: "Settings",
        description:
          "Manage asset settings, including types, fields, readings, and other configurations.",
      },
      {
        key: "manage_assets_maintenance",
        name: "Maintenance",
        description: "Create, edit, and delete maintenance programs.",
      },
      {
        key: "manage_assets",
        name: "Manage",
        description:
          "Create, edit, archive, and delete assets, including recording the last service information.",
      },
      {
        key: "manage_assets_media",
        name: "Manage media",
        description:
          "Add and delete images and documents associated with assets",
      },
      {
        key: "view_assets",
        name: "Access to the Assets feature",
        description:
          "Access to the Assets feature. If unselected, users can still add assets to inspections and actions.",
      },
    ],
  },
  {
    heading: "Heads Up",
    requisiteOptions: ["createHeadsUp"],
    options: [
      {
        key: "create_heads_up",
        name: "Create",
        description:
          "Create, edit, and delete Heads Ups. Each user can only manage the Heads Ups that they've created.",
      },
      {
        key: "manage_heads_up",
        name: "Manage",
        description: "Create, edit, and delete Heads Ups.",
      },
    ],
  },
  {
    heading: "Analytics",
    requisiteOptions: [
      "viewAnalytics",
      "manageAnalytics",
      "createSiteAnalytics",
      "createAnalytics",
    ],
    options: [
      {
        key: "manage_analytics",
        name: "Manage",
        description:
          "Create, edit, and delete all Analytics dashboards in your organization, including custom dashboards.",
      },
      {
        key: "create_site_analytics",
        name: "Create dashboards (site-based)",
        description:
          "Create Analytics dashboards that show data based on site membership, and edit and delete dashboards that you’ve created.",
      },
      {
        key: "create_analytics",
        name: "Create dashboards (individual-based)",
        description:
          "Create Analytics dashboards showing data based on the creator, and edit and delete dashboards that you’ve created.",
      },
      {
        key: "view_analytics",
        name: "View",
        description: "View Analytics dashboards you have access to.",
      },
    ],
  },
  {
    heading: "Documents",
    options: [
      {
        key: "admin_resources",
        name: "Settings",
        description: "Manage tags for files",
      },
    ],
  },
  {
    heading: "Lone Worker",
    options: [
      {
        key: "settings_lone_worker",
        name: "Settings",
        description:
          "Manage lone worker settings, including the lone worker timer and notifications.",
      },
      {
        key: "manage_lone_worker",
        name: "Manage",
        description: "Create, edit, and delete lone worker profiles.",
      },
      {
        key: "access_lone_worker",
        name: "Access",
        description: "Access the Lone Worker feature to check in and out.",
      },
    ],
  },
  {
    heading: "Companies",
    options: [
      {
        key: "manage_companies",
        name: "Manage",
        description: "Create, edit, and delete companies in your organization.",
      },
    ],
  },
  {
    heading: "Data Management",
    options: [
      {
        key: "allow_modify_manage_data",
        name: "Manage all data",
        description:
          "Edit, archive, and delete all templates, inspections, issues, and actions. Overrides any existing access rules.",
      },
      {
        key: "manage_data",
        name: "View all data",
        description:
          "View all templates, inspections, and actions on the web app. Overrides any existing access rules.",
      },
      {
        key: "allow_mobile_data_access_sync",
        name: "Enable for mobile app",
        description: "Enable data access to sync to both web and mobile app.",
      },
    ],
  },
  {
    heading: "Investigations",
    options: [
      {
        key: "access_investigations",
        name: "Access",
        description:
          "Access the Investigations feature to create and view investigations",
      },
      {
        key: "manage_investigations",
        name: "Manage",
        description:
          "Manage investigation settings, including custom statuses.",
      },
    ],
  },
];

interface PermissionsDataGridProps {
  permissions: Array<[string, boolean]>;
}

export default function PermissionsDataGrid({
  permissions,
}: PermissionsDataGridProps) {
  let idCounter = 1;
  const enrichedData: Array<{
    id: number;
    heading: string;
    option: string;
    description?: string;
    rawOption: string;
    enabled: boolean;
  }> = PermissionOptionsList.flatMap((group) =>
    group.options.map((option) => {
      const [key, enabled] = permissions.find(
        ([permissionKey]) => permissionKey === option.key,
      ) || ["N/A", false];
      return {
        id: idCounter++,
        heading: group.heading,
        option: option.name,
        rawOption: key,
        enabled,
        description: option.description,
      };
    }),
  );

  permissions.forEach(([key, enabled]) => {
    if (
      !PermissionOptionsList.some((group) =>
        group.options.some((option) => option.key === key),
      )
    ) {
      enrichedData.push({
        id: idCounter++,
        heading: "Uncategorized",
        option: key, // Use the key as the option name for unknown permissions
        description: key,
        rawOption: key,
        enabled,
      });
    }
  });

  return (
    <Box sx={{ height: "70vh", width: "100%" }}>
      <DataGrid density="compact" rows={enrichedData} columns={columns} />
    </Box>
  );
}
